import { useState, useRef, useEffect } from "react"
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Tooltip, Slider } from "@mui/material"
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Search as SearchIcon,
} from "@mui/icons-material"

const ImageViewerModal = ({ open, onClose, imageUrl, title }) => {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [magnifyMode, setMagnifyMode] = useState(false)
  const [magnifyPosition, setMagnifyPosition] = useState({ x: 0, y: 0 })
  const imageContainerRef = useRef(null)
  const imageRef = useRef(null)

  // Reset position and scale when modal opens
  useEffect(() => {
    if (open) {
      setScale(1)
      setPosition({ x: 0, y: 0 })
      setMagnifyMode(false)
    }
  }, [open])

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 5))
  }

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  const handleScaleChange = (event, newValue) => {
    setScale(newValue)
  }

  const handleMouseDown = (e) => {
    if (magnifyMode) return
    setIsDragging(true)
    setStartPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    })
  }

  const handleMouseMove = (e) => {
    if (magnifyMode) {
      // Handle magnify glass positioning
      if (imageContainerRef.current) {
        const rect = imageContainerRef.current.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width) * 100
        const y = ((e.clientY - rect.top) / rect.height) * 100
        setMagnifyPosition({ x, y })
      }
      return
    }

    if (!isDragging) return
    setPosition({
      x: e.clientX - startPosition.x,
      y: e.clientY - startPosition.y,
    })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const toggleMagnifyMode = () => {
    setMagnifyMode(!magnifyMode)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6">{title || "Document Image"}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Toggle Magnify Mode">
            {/* <IconButton
              onClick={toggleMagnifyMode}
              color={magnifyMode ? "primary" : "default"}
              aria-label="toggle magnify mode"
            >
              <SearchIcon />
            </IconButton> */}
          </Tooltip>
          <Tooltip title="Zoom Out">
            <IconButton onClick={handleZoomOut} aria-label="zoom out">
              <ZoomOutIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ width: 100, mx: 1 }}>
            <Slider
              value={scale}
              min={0.5}
              max={5}
              step={0.1}
              onChange={handleScaleChange}
              aria-labelledby="zoom-slider"
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            />
          </Box>
          <Tooltip title="Zoom In">
            <IconButton onClick={handleZoomIn} aria-label="zoom in">
              <ZoomInIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          position: "relative",
          height: "70vh",
          overflow: "hidden",
          p: 0,
          backgroundColor: "#f5f5f5",
          cursor: magnifyMode ? "none" : isDragging ? "grabbing" : "grab",
        }}
      >
        <Box
          ref={imageContainerRef}
          sx={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            ref={imageRef}
            sx={{
              position: "absolute",
              transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
              transition: isDragging ? "none" : "transform 0.1s ease",
              transformOrigin: "center",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          >
            <img
              src={imageUrl || "/placeholder.svg?height=400&width=400"}
              alt="Document"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
              onError={(e) => {
                console.error("Image failed to load")
                e.target.src = "/placeholder.svg?height=400&width=400"
              }}
            />
          </Box>

          {/* Magnifying glass */}
          {magnifyMode && (
            <>
              {/* Cursor indicator */}
              <Box
                sx={{
                  position: "absolute",
                  left: `${magnifyPosition.x}%`,
                  top: `${magnifyPosition.y}%`,
                  width: "100px",
                  height: "100px",
                  border: "2px solid #1976d2",
                  borderRadius: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />

              {/* Magnified view */}
              <Box
                sx={{
                  position: "absolute",
                  right: 20,
                  bottom: 20,
                  width: "200px",
                  height: "200px",
                  border: "3px solid #1976d2",
                  borderRadius: "50%",
                  overflow: "hidden",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                  zIndex: 3,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${imageUrl || "/placeholder.svg?height=400&width=400"})`,
                    backgroundPosition: `${magnifyPosition.x}% ${magnifyPosition.y}%`,
                    backgroundSize: `${scale * 500}%`,
                    backgroundRepeat: "no-repeat",
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ImageViewerModal
